<script>
    import {storePacks, subscriptions, rechargeRewardsEvent, isMobile} from '../stores';
    import {getContext} from 'svelte';
    import Loader from '../lib/Loader.svelte';
    import StorePack from './StorePack.svelte';
    import FreePack from './FreePack.svelte';
    import SubscriptionPack from './SubscriptionPack.svelte';
    import GoldRushPack from "./GoldRushPack.svelte";
    import PackDetailSheet from './PackDetailSheet.svelte';
    import {vipLevel} from '../stores';
    import {isPrimetimePendingStart, getNextPackTransition} from '../utils.js';
    import {freeWebshopPacks} from './free-packs.js';

    let { tabIndex = 0, storeSection = '', title = '' } = $props();

    // Almost every section gets the compact 2-up mobile treatment. goldRush is its
    // own bespoke component; giftFriend renders its full desktop card on mobile too
    // (single column) — the bespoke gift-box art, itemised rewards and the
    // recipient-search purchase flow don't fit the compact card / detail-sheet model.
    // Subscriptions/resources use the compact SubscriptionPack.
    const NON_COMPACT_SECTIONS = ['goldRush', 'giftFriend'];
    let compactCards = $derived($isMobile && !NON_COMPACT_SECTIONS.includes(storeSection));

    // The pack whose detail sheet is open (mobile only), or null.
    let selectedPack = $state(null);
    function openDetail(payload) {
        selectedPack = payload;
    }
    function closeDetail() {
        selectedPack = null;
    }

    const {buyPack: buyPackFromContext} = getContext('buyPack');
    // Wrap the context handler to adapt from callback prop style (direct data) to the old event.detail style
    function handleBuyPack(data) {
        buyPackFromContext({detail: data});
    }
    function buyFromSheet({storePack, packDetails}) {
        closeDetail();
        handleBuyPack({storePack, packDetails});
    }

    let rerenderTrigger = $state(0);
    let expirationTimeout = null;

    // Filter out expired and not-yet-started primeTime packs, re-evaluated when rerenderTrigger changes
    let activePacks = $derived.by(() => {
        rerenderTrigger; // Reference triggers reactivity
        const now = Date.now();
        return $storePacks?.filter(pack =>
            (!pack.endAt || new Date(pack.endAt).getTime() > now) && !isPrimetimePendingStart(pack, now)
        ) || [];
    });

    function schedulePackExpiration() {
        if (expirationTimeout) clearTimeout(expirationTimeout);

        const now = Date.now();
        // Use the raw store so pending primeTime packs (excluded from activePacks) are considered
        const nextTransition = getNextPackTransition($storePacks, now);

        if (nextTransition === null) return;

        // Clamp to the setTimeout max (~24.8 days) — larger delays fire immediately
        const delay = Math.min(nextTransition - now + 1000, 2 ** 31 - 1);

        expirationTimeout = setTimeout(() => {
            rerenderTrigger = Date.now(); // Forces re-evaluation
            schedulePackExpiration(); // Schedule next expiration
        }, delay);
    }

    $effect(() => {
        schedulePackExpiration();
        return () => {
            if (expirationTimeout) clearTimeout(expirationTimeout);
        };
    });

    function subscriptionCondition(subscription) {
        // Purchased subscriptions are of 2 types: 
        // - normal (that gives items)  
        // - resources (that gives a single resource)

        // The resources subscriptions are missing the rewards.items property in the object, so we can use that to differentiate between the two types

        if (storeSection === 'subscriptions') {
            // This check is to ignore purchased resources from the subscriptions tab
            return subscription.rewards.items !== undefined && subscription.rewards.items.length > 1;
        } else {
            // This check is to ignore purchased subscriptions from the resources tab
            return subscription.rewards.items === undefined || subscription.rewards.items.length === 1
        }
    }

</script>
<div
        class="tab-panel"
        id="panel-{tabIndex}"
        role="tabpanel"
        tabindex={tabIndex}
>
    <h1>{title}</h1>
    <div class="grid {storeSection}" class:compact-grid={compactCards}>
        {#if storeSection === 'promos' ||  storeSection === 'freeGifts'}
            {#await $subscriptions.filter((s) => freeWebshopPacks.includes(s.bundleName)) then value}
                {#each value as subscription, i (subscription.bundleName + '-' + i)}
                    <FreePack subscription={subscription} compact={compactCards}/>
                {/each}
            {/await}
        {/if}

        {#if storeSection === 'subscriptions' || storeSection === 'resources'}
            {#await $subscriptions.filter((s) => !freeWebshopPacks.includes(s.bundleName)) then value}
                {#each value as subscription, i (subscription.bundleName + '-' + i)}
                    {#if subscriptionCondition(subscription) }
                        <SubscriptionPack subscription={subscription} isPurchased={true} storeSection={storeSection} compact={compactCards} onOpenDetail={openDetail}/>
                    {/if}
                {/each}
            {/await}
        {/if}

        {#if activePacks != null}
            {#await activePacks.reduce((acc, pack) => {
                // Find the featured pack with lowest position (only once)
                if (!acc.lowestFeaturedPack) {
                    const featuredPacks = activePacks.filter(p => {
                        if (!p.isFeatured) return false;
                        // Include promos or goldPacks with primetime
                        return p.storeSection === 'promos' ||
                               (p.storeSection === 'goldPacks' && p.specialTitle && p.specialTitle.toLowerCase().includes('primetime'));
                    });
                    if (featuredPacks.length > 0) {
                        acc.lowestFeaturedPack = featuredPacks.sort((a, b) => {
                            // First sort by position (lowest first)
                            const positionDiff = (a.position || 0) - (b.position || 0);
                            if (positionDiff !== 0) return positionDiff;

                            // If positions are equal, prioritize showPlayerName: true
                            if (a.showPlayerName && !b.showPlayerName) return -1;
                            if (!a.showPlayerName && b.showPlayerName) return 1;

                            return 0;
                        })[0];
                    }
                }

                // For goldPacks section, separate all packs by type and price tier
                if (storeSection === 'goldPacks' && pack.storeSection === storeSection) {
                    const isPrimetime = pack.specialTitle && pack.specialTitle.toLowerCase().includes('primetime');
                    const isDoubleGold = pack.specialTitle && pack.specialTitle.toLowerCase().includes('doublegoldpack');
                    const priceAmount = pack.paymentHub?.price?.amount || 0;

                    // Store all packs by type - primetime can have multiple packs per tier
                    // Exclude if it's the featured pack
                    if (isPrimetime && pack !== acc.lowestFeaturedPack) {
                        acc.primetimePacks.push(pack);
                        acc.primetimeTiers.add(priceAmount);
                    } else if (isDoubleGold) {
                        acc.doubleByTier.set(priceAmount, pack);
                    } else {
                        acc.regularByTier.set(priceAmount, pack);
                    }
                } else if (storeSection === 'battlePass' && pack.storeSection === 'specialEvents'
                    && pack.perks?.some(p => p.startsWith('battlePass')) && !pack.hasEventPass && pack !== acc.lowestFeaturedPack) {
                    // Virtual battlePass section: match battle pass packs from specialEvents
                    acc.packs.push(pack);
                } else if (storeSection === 'eventImagePacks' && pack.storeSection === 'specialEvents'
                    && (pack.bundleName?.startsWith('fortunepack') || pack.bundleName?.startsWith('wheeloffortune'))
                    && pack !== acc.lowestFeaturedPack) {
                    // Virtual eventImagePacks section: fortune teller + wheel of fortune
                    acc.packs.push(pack);
                } else if (pack.storeSection === storeSection && pack !== acc.lowestFeaturedPack
                    && !(storeSection === 'specialEvents' && pack.perks?.some(p => p.startsWith('battlePass')))
                    && !(storeSection === 'specialEvents' && (pack.bundleName?.startsWith('fortunepack') || pack.bundleName?.startsWith('wheeloffortune')))) {
                    // Exclude battle pass, fortune, wheel packs from specialEvents
                    acc.packs.push(pack);
                }
                return acc;
            }, {
                packs: [],
                primetimePacks: [],
                primetimeTiers: new Set(),
                doubleByTier: new Map(),
                regularByTier: new Map(),
                lowestFeaturedPack: null
            })}
                <Loader/>
            {:then result}
                {#each (storeSection === 'goldPacks' ? (
                    (() => {
                        const displayPacks = [];

                        // First, add ALL primetime packs
                        displayPacks.push(...result.primetimePacks);

                        // Then, for tiers without primetime, add double or regular
                        const allTiers = new Set([
                            ...result.primetimeTiers,
                            ...result.doubleByTier.keys(),
                            ...result.regularByTier.keys()
                        ]);

                        allTiers.forEach(tier => {
                            // Only add double/regular if this tier has NO primetime packs
                            if (!result.primetimeTiers.has(tier)) {
                                if (result.doubleByTier.has(tier)) {
                                    displayPacks.push(result.doubleByTier.get(tier));
                                } else if (result.regularByTier.has(tier)) {
                                    displayPacks.push(result.regularByTier.get(tier));
                                }
                            }
                        });

                        // Sort by payment price amount
                        return displayPacks.sort((a, b) => {
                            const priceA = a.paymentHub?.price?.amount || 0;
                            const priceB = b.paymentHub?.price?.amount || 0;
                            return priceA - priceB;
                        });
                    })()
                ) : result.packs) as storePack, i (storePack.bundleName + '-' + i)}
                    {#if storeSection === 'subscriptions' || storeSection === 'resources'}
                        <SubscriptionPack subscription={storePack} isPurchased={false} storeSection={storeSection}
                                          onbuyPack={handleBuyPack} compact={compactCards} onOpenDetail={openDetail}/>
                    {:else if storeSection === 'vip'}
                        {#if storePack.bundleName.match(/lvl(\d+)pack/)[1] <= $vipLevel}
                            <StorePack {storePack} {storeSection} onbuyPack={handleBuyPack} compact={compactCards} onOpenDetail={openDetail}/>
                        {/if}
                    {:else if !storePack.luckyDraw}
                        <StorePack {storePack} {storeSection} onbuyPack={handleBuyPack} compact={compactCards} onOpenDetail={openDetail}/>
                    {/if}
                {/each}
            {/await}
        {/if}

        {#if storeSection === 'goldRush'}
            <GoldRushPack event={$rechargeRewardsEvent}/>
        {/if}

        {#if activePacks != null && storeSection === 'giftFriend'}
            {#await activePacks.filter((pack) => pack.buyForAFriend === true)}
                <Loader/>
            {:then unsortedPacks}
                {#each unsortedPacks.sort((a, b) => {
                    // If a has holidayTheme and b does not, a should come first
                    if (a.holidayTheme && !b.holidayTheme) return -1;
                    // If b has holidayTheme and a does not, b should come first
                    if (b.holidayTheme && !a.holidayTheme) return 1;
                    // If both have holidayTheme or neither have, maintain original order
                    return 0;
                }) as storePack, i (storePack.bundleName + '-' + i)}
                    <StorePack {storePack} {storeSection} onbuyPack={handleBuyPack} compact={compactCards} onOpenDetail={openDetail}/>
                {/each}
            {/await}
        {/if}

        {#if activePacks != null && storeSection === 'luckyDraw'}
            {#await activePacks.reduce((acc, pack) => {
                // Find the featured pack with lowest position (only once)
                if (!acc.lowestFeaturedPack) {
                    const featuredPacks = activePacks.filter(p => {
                        if (!p.isFeatured) return false;
                        // Include promos or goldPacks with primetime
                        return p.storeSection === 'promos' ||
                               (p.storeSection === 'goldPacks' && p.specialTitle && p.specialTitle.toLowerCase().includes('primetime'));
                    });
                    if (featuredPacks.length > 0) {
                        acc.lowestFeaturedPack = featuredPacks.sort((a, b) => {
                            // First sort by position (lowest first)
                            const positionDiff = (a.position || 0) - (b.position || 0);
                            if (positionDiff !== 0) return positionDiff;

                            // If positions are equal, prioritize showPlayerName: true
                            if (a.showPlayerName && !b.showPlayerName) return -1;
                            if (!a.showPlayerName && b.showPlayerName) return 1;

                            return 0;
                        })[0];
                    }
                }

                // Include lucky draw packs, but exclude the featured pack with lowest position
                if (pack.luckyDraw && pack !== acc.lowestFeaturedPack) {
                    acc.packs.push(pack);
                }
                return acc;
            }, {packs: [], lowestFeaturedPack: null}).packs}
                <Loader/>
            {:then value}
                {#each value as storePack, i (storePack.bundleName + '-' + i)}
                    <StorePack {storePack} {storeSection} onbuyPack={handleBuyPack} compact={compactCards} onOpenDetail={openDetail}/>
                {/each}
            {/await}
        {/if}

        {#if activePacks != null && storeSection === 'featured'}
            {#each activePacks
                .filter(pack => {
                    if (!pack.isFeatured) return false;

                    // Include promos packs
                    if (pack.storeSection === 'promos') return true;

                    // Include goldPacks with primetime specialTitle
                    if (pack.storeSection === 'goldPacks' && pack.specialTitle && pack.specialTitle.toLowerCase().includes('primetime')) {
                        return true;
                    }

                    return false;
                })
                .sort((a, b) => {
                    // First sort by position (lowest first)
                    const positionDiff = (a.position || 0) - (b.position || 0);
                    if (positionDiff !== 0) return positionDiff;

                    // If positions are equal, prioritize showPlayerName: true
                    if (a.showPlayerName && !b.showPlayerName) return -1;
                    if (!a.showPlayerName && b.showPlayerName) return 1;

                    return 0;
                })
                .slice(0, 1) as storePack, i (storePack.bundleName + '-' + i)}
                {#key storePack.bundleName}
                    <StorePack {storePack} {storeSection} onbuyPack={handleBuyPack} compact={compactCards} onOpenDetail={openDetail}/>
                {/key}
            {/each}
        {/if}
    </div>
</div>

<PackDetailSheet pack={selectedPack} onClose={closeDetail} onBuy={buyFromSheet}/>

<style lang="scss">
  @use '../styles/vars.scss' as *;
  @use '../styles/breakpoints.scss' as bp;

  .tab-panel {
    text-align: center;

    h1 {
      clip: rect(1px, 1px, 1px, 1px);
      clip-path: inset(50%);
      height: 1px;
      width: 1px;
      margin: -1px;
      overflow: hidden;
      padding: 0;
      position: absolute;
    }

    .grid {
      padding-top: 10px;
      padding-left: 10px;
      padding-right: 10px;
      display: grid;
      grid-template-columns: repeat(
        auto-fit,
                      minmax($store-pack-width, $store-pack-width)
      );
      grid-auto-rows: $store-pack-height;
      gap: 15px;
      place-items: center;
      justify-content: center;
      align-items: start;
      justify-items: center;

      &.goldRush {
        grid-auto-rows: $store-pack-height-gold-rush;
        grid-template-columns: repeat(auto-fit, minmax(877px, 877px));
      }

      &.goldPacks {
        grid-auto-rows: $store-pack-height-gold-packs;
      }

      &.resources {
        grid-auto-rows: $store-pack-height-resources;
      }

      &.subscriptions {
        grid-auto-rows: $store-pack-height-subscriptions;
      }

      &.giftFriend, &.specialEvents {
        grid-auto-rows: $store-pack-height-gift-friend;
      }

      &.luckyDraw {
        grid-auto-rows: $store-pack-height-lucky-draw;
      }

      @media (max-width: 700px) {
        grid-template-columns: repeat(
          auto-fit,
                        minmax($store-pack-width, 0.8fr)
        );
      }

      // Mobile 2-up compact grid (StorePack compact cards). Desktop untouched.
      // minmax(0, 1fr) lets columns shrink below their content's min-width so an
      // in-flow item-preview row can't inflate the card past 50% of the row.
      &.compact-grid {
        @include bp.mobile {
          grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
          grid-auto-rows: auto;
          align-items: stretch;
          justify-items: stretch;
          gap: 10px;
          padding-left: 8px;
          padding-right: 8px;
        }
      }
    }
  }
</style>
